import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea,
} from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { listPartners } from "@/features/partners/api";
import { listUsers, userLabel } from "@/features/users/api";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { createQuotation, type Quotation, type QuotationInput } from "./api";

/** select で「未設定」を表す内部キー（空文字は HeroUI が扱えないため）。 */
const NONE_KEY = "__none__";

/** 今日（ローカル時刻）を YYYY-MM-DD で返す。 */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * 見積の新規登録ダイアログ。
 *
 * **押した瞬間には起票しない**。見積番号は起票と同時に採番されるため、ボタンを
 * 押しただけで番号を消費すると、取りやめたときに中身の無い見積が残ってしまう。
 * ここで入力してから登録する。
 *
 * 得意先だけ必須。見積は「誰に出すか」が決まっていないと意味を持たないため。
 * 残りは依頼を受けた時点では決まっていないことが多いので任意にし、見積管理画面で
 * 埋めていく。状態は「依頼受領」から始まる（API の既定）ので、ここでは選ばせない。
 *
 * 営業担当は**ログイン中のユーザー**を既定にする。自分が受けた依頼を自分で
 * 起票するのが通常の流れで、毎回選び直させる意味が薄いため。
 */
export default function QuotationCreateDialog({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (quotation: Quotation) => void;
}) {
  const { user } = useAuth();
  const [clientId, setClientId] = useState("");
  const [salesRepId, setSalesRepId] = useState("");
  const [requestedAt, setRequestedAt] = useState(today());
  const [quotedDate, setQuotedDate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [note, setNote] = useState("");
  // 客先の依頼を受け取った時点で分かっている項目。残りは詳細画面で足せるので、
  // ここに置くのは起票時に手元にあるものだけにする。
  const [clientRequestNumber, setClientRequestNumber] = useState("");
  const [subject, setSubject] = useState("");
  const [answerDueDate, setAnswerDueDate] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 開くたびに初期値へ戻す（前回の入力を持ち越さない）。
  useEffect(() => {
    if (!isOpen) return;
    setClientId("");
    setSalesRepId(user?.user_id ?? "");
    setRequestedAt(today());
    setQuotedDate("");
    setValidUntil("");
    setNote("");
    setClientRequestNumber("");
    setSubject("");
    setAnswerDueDate("");
    setIsUrgent(false);
    setError(null);
    setSaving(false);
  }, [isOpen, user]);

  // 得意先は取引中のものだけを候補に出す（取引を止めた相手を選ばせない）。
  const partners = useQuery({
    queryKey: ["partners", "picker"],
    queryFn: () => listPartners({ disabled: false, isClient: true, limit: 500, offset: 0 }),
    enabled: isOpen,
  });
  const users = useQuery({
    queryKey: ["users", "picker"],
    queryFn: () => listUsers(),
    enabled: isOpen,
  });

  const submit = async () => {
    if (!clientId) {
      setError("得意先を選択してください。");
      return;
    }

    const input: QuotationInput = {
      client_id: Number(clientId),
      sales_rep_id: salesRepId || null,
      requested_at: requestedAt || null,
      quoted_date: quotedDate || null,
      valid_until: validUntil || null,
      note: note.trim() || null,
      client_request_number: clientRequestNumber.trim() || null,
      subject: subject.trim() || null,
      answer_due_date: answerDueDate || null,
      is_urgent: isUrgent,
    };

    setSaving(true);
    try {
      onCreated(await createQuotation(input));
      onClose();
    } catch (e) {
      setError(errorMessage(e, "見積を起票できませんでした。"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>見積の新規登録</span>
          <span className="text-small font-normal text-default-500">
            見積番号は登録時に自動で採番されます。得意先以外は任意で、あとから見積管理画面で変更できます。
          </span>
        </ModalHeader>

        <ModalBody>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              label="得意先"
              labelPlacement="outside"
              placeholder="選択してください"
              aria-label="得意先"
              variant="bordered"
              radius="sm"
              isRequired
              isLoading={partners.isPending}
              selectedKeys={clientId ? [clientId] : []}
              onSelectionChange={(keys) => {
                setClientId(([...keys][0] as string | undefined) ?? "");
                setError(null);
              }}
            >
              {(partners.data?.items ?? []).map((p) => (
                <SelectItem key={String(p.partner_id)}>
                  {`${p.partner_code} ${p.partner_name}`}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="営業担当"
              labelPlacement="outside"
              placeholder="（未設定）"
              aria-label="営業担当"
              variant="bordered"
              radius="sm"
              isLoading={users.isPending}
              selectedKeys={[salesRepId || NONE_KEY]}
              disallowEmptySelection
              onSelectionChange={(keys) => {
                const key = [...keys][0] as string | undefined;
                setSalesRepId(!key || key === NONE_KEY ? "" : key);
              }}
            >
              {[
                { key: NONE_KEY, label: "（未設定）" },
                ...(users.data?.items ?? []).map((u) => ({
                  key: u.user_id,
                  label: userLabel(u),
                })),
              ].map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>

            <Input
              label="依頼受領日"
              labelPlacement="outside"
              placeholder=" "
              aria-label="依頼受領日"
              type="date"
              variant="bordered"
              radius="sm"
              value={requestedAt}
              onValueChange={setRequestedAt}
            />
            <Input
              label="見積日"
              labelPlacement="outside"
              placeholder=" "
              aria-label="見積日"
              type="date"
              variant="bordered"
              radius="sm"
              value={quotedDate}
              onValueChange={setQuotedDate}
            />
            <Input
              label="有効期限"
              labelPlacement="outside"
              placeholder=" "
              aria-label="有効期限"
              type="date"
              variant="bordered"
              radius="sm"
              value={validUntil}
              onValueChange={setValidUntil}
            />
            <Input
              label="客先依頼番号"
              labelPlacement="outside"
              placeholder=" "
              aria-label="客先依頼番号"
              variant="bordered"
              radius="sm"
              value={clientRequestNumber}
              description="客先への回答や問い合わせの照合に使います。"
              onValueChange={setClientRequestNumber}
            />
            <Input
              label="件名"
              labelPlacement="outside"
              placeholder=" "
              aria-label="件名"
              variant="bordered"
              radius="sm"
              value={subject}
              onValueChange={setSubject}
            />
            <Input
              label="回答期限"
              labelPlacement="outside"
              placeholder=" "
              aria-label="回答期限"
              type="date"
              variant="bordered"
              radius="sm"
              value={answerDueDate}
              description="客先に回答する期限です（有効期限とは別）。"
              onValueChange={setAnswerDueDate}
            />
            <Select
              label="特急"
              labelPlacement="outside"
              aria-label="特急"
              variant="bordered"
              radius="sm"
              disallowEmptySelection
              selectedKeys={[isUrgent ? "true" : "false"]}
              onSelectionChange={(keys) => {
                const key = [...keys][0];
                if (key == null) return;
                setIsUrgent(String(key) === "true");
              }}
            >
              <SelectItem key="false">通常</SelectItem>
              <SelectItem key="true">特急</SelectItem>
            </Select>
          </div>

          <Textarea
            label="備考"
            labelPlacement="outside"
            placeholder=" "
            aria-label="備考"
            variant="bordered"
            radius="sm"
            minRows={2}
            value={note}
            onValueChange={setNote}
          />

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="quotation-submit"
            isLoading={saving}
            onPress={() => void submit()}
          >
            登録
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
